<script>
import moment from 'moment'
import appConfig from '@src/app.config'
import { crudMethods } from '@state/helpers'

export default {
  page: {
    title: 'Dashboard',
    meta: [{ name: 'description', content: appConfig.description }],
  },

  data() {
    return {
      user: this.$store ? this.$store.state.auth.currentUser : {} || {},

      data: [],

      dataPie: {
        chartOptions: {
          chart: {
            type: 'pie',
            toolbar: {
              show: false,
            },
          },

          colors: [
            '#dc2626',
            '#2563eb',
            '#0891b2',
            '#64748b',
            '#f59e0b',
            '#7c3aed',
            '#059669',
            '#db2777',
            '#0f766e',
            '#ea580c',
            '#4f46e5',
            '#0284c7',
            '#65a30d',
            '#c026d3',
            '#475569',
            '#ca8a04',
            '#be123c',
            '#0369a1',
            '#15803d',
            '#9333ea',
          ],

          stroke: {
            width: 2,
            colors: ['#ffffff'],
          },

          labels: [],

          /*
           * Legend sengaja TIDAK diberi height.
           * Biar seluruh Witel tetap tampil.
           */
          legend: {
            show: true,
            position: 'bottom',
            horizontalAlign: 'center',

            fontSize: '10px',
            fontWeight: 500,

            itemMargin: {
              horizontal: 7,
              vertical: 4,
            },

            markers: {
              width: 8,
              height: 8,
              radius: 4,
            },

            labels: {
              colors: '#64748b',
            },
          },

          dataLabels: {
            enabled: true,

            style: {
              fontSize: '10px',
              fontWeight: 600,
            },

            dropShadow: {
              enabled: false,
            },
          },

          tooltip: {
            y: {
              formatter: (value) => {
                const number = Math.abs(Number(value))

                if (number >= 1.0e9) {
                  return `Rp${(number / 1.0e9).toFixed(2)} M`
                }

                if (number >= 1.0e6) {
                  return `Rp${(number / 1.0e6).toFixed(2)} Jt`
                }

                if (number >= 1.0e3) {
                  return `Rp${(number / 1.0e3).toFixed(2)} Rb`
                }

                return `Rp${number.toLocaleString('id-ID')}`
              },
            },
          },

          responsive: [
            {
              breakpoint: 991,
              options: {
                chart: {
                  height: 350,
                },

                legend: {
                  fontSize: '10px',
                  itemMargin: {
                    horizontal: 5,
                    vertical: 3,
                  },
                },
              },
            },
          ],
        },

        series: [],
      },

      year: moment().format('YYYY'),
      years: [],
    }
  },

  mounted() {
    this.getData()
    this.getListData()

    for (let i = Number(this.year); i >= 2022; i--) {
      this.years.push(i)
    }
  },

  methods: {
    ...crudMethods,

    getData() {
      this.listData({
        url: `web/dashboard/pie-revenue-witel?year=${this.year}`,
      }).then((response) => {
        const { data } = response.data

        this.setPieChartData(data)
      })
    },

    getListData() {
      this.listData({
        url: `web/dashboard/revenue-witel?year=${this.year}`,
      }).then((response) => {
        this.data = response.data.data || []
      })
    },

    setPieChartData(data) {
      this.dataPie = {
        ...this.dataPie,

        series: data.series || [],

        chartOptions: {
          ...this.dataPie.chartOptions,
          labels: data.labels || [],
        },
      }
    },

    changeYear() {
      this.getData()
      this.getListData()
    },

    formatRupiah(value) {
      return `Rp${Number(value || 0)
        .toFixed()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`
    },

    getTotal(field) {
      return this.data.reduce(
        (total, item) => total + Number(item[field] || 0),
        0
      )
    },

    getFeeTotal() {
      return this.data.reduce(
        (total, item) => total + Number(item.spk || 0) - Number(item.sph || 0),
        0
      )
    },
  },
}
</script>

<template>
  <fragment>
    <div class="row">
      <div class="col-xl-12">
        <div class="dashboard-card">
          <!-- HEADER -->
          <div class="dashboard-card-header">
            <div class="dashboard-title">
              <h5>Revenue Per Witel</h5>

              <p>
                Distribusi nilai kontrak berdasarkan Witel
              </p>
            </div>

            <div class="year-filter">
              <label>Tahun</label>

              <b-form-select
                v-model="year"
                :options="years"
                @change="changeYear"
              />
            </div>
          </div>

          <!-- CONTENT -->
          <div class="dashboard-content">
            <!-- CHART -->
            <div class="chart-section">
              <div class="chart-container">
                <apexchart
                  v-if="dataPie.series.length > 0"
                  height="390"
                  :series="dataPie.series"
                  :options="dataPie.chartOptions"
                />
              </div>
            </div>

            <!-- TABLE -->
            <div class="table-section">
              <div class="revenue-table">
                <!-- TABLE HEADER -->
                <div class="table-head">
                  <div class="col-witel">
                    Witel
                  </div>

                  <div>
                    Nilai Kontrak
                  </div>

                  <div>
                    Nilai Kewajiban
                  </div>

                  <div>
                    Fee Kopeg
                  </div>
                </div>

                <!-- TABLE BODY -->
                <div class="table-body">
                  <div
                    v-for="(item, index) in data"
                    :key="index"
                    class="table-row"
                  >
                    <div class="col-witel">
                      {{ item.witel }}
                    </div>

                    <div>
                      {{ formatRupiah(item.spk) }}
                    </div>

                    <div>
                      {{ formatRupiah(item.sph) }}
                    </div>

                    <div class="fee-value">
                      {{ formatRupiah(item.spk - item.sph) }}
                    </div>
                  </div>
                </div>

                <!-- TOTAL -->
                <div class="table-total">
                  <div class="col-witel">
                    Grand Total
                  </div>

                  <div>
                    {{ formatRupiah(getTotal('spk')) }}
                  </div>

                  <div>
                    {{ formatRupiah(getTotal('sph')) }}
                  </div>

                  <div>
                    {{ formatRupiah(getFeeTotal()) }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </fragment>
</template>

<style lang="scss" scoped>
/* =========================================================
   CARD
========================================================= */

.dashboard-card {
  overflow: hidden;

  margin-bottom: 20px;

  border: 1px solid #e5e7eb;
  border-radius: 14px;

  background: #ffffff;

  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.04),
    0 1px 3px rgba(15, 23, 42, 0.03);
}

/* =========================================================
   HEADER
========================================================= */

.dashboard-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;

  min-height: 70px;

  padding: 15px 20px;

  border-bottom: 1px solid #eef2f7;
}

.dashboard-title {
  h5 {
    margin: 0 0 3px;

    color: #1e293b;

    font-size: 15px;
    font-weight: 700;
  }

  p {
    margin: 0;

    color: #94a3b8;

    font-size: 11px;
  }
}

/* =========================================================
   YEAR
========================================================= */

.year-filter {
  display: flex;
  align-items: center;

  gap: 8px;

  label {
    margin: 0;

    color: #64748b;

    font-size: 11px;
    font-weight: 600;
  }

  select {
    width: 100px;
    height: 34px;

    padding: 4px 10px;

    border: 1px solid #dbe2ea;
    border-radius: 7px;

    color: #334155;

    background: #ffffff;

    font-size: 12px;

    box-shadow: none;

    &:focus {
      border-color: #dc2626;

      box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.08);
    }
  }
}

/* =========================================================
   CONTENT
========================================================= */

.dashboard-content {
  display: grid;

  grid-template-columns: 43% 57%;

  min-height: 430px;
}

/* =========================================================
   CHART
========================================================= */

.chart-section {
  display: flex;

  align-items: flex-start;
  justify-content: center;

  padding: 12px 20px 18px;

  border-right: 1px solid #eef2f7;
}

.chart-container {
  width: 100%;
}

/*
|--------------------------------------------------------------------------
| Apex legend
|--------------------------------------------------------------------------
|
| Tidak ada max-height.
| Legend boleh memanjang sesuai jumlah Witel.
|
*/

::v-deep .apexcharts-legend {
  padding-top: 4px !important;
}

::v-deep .apexcharts-legend-text {
  color: #64748b !important;

  font-size: 10px !important;
  font-weight: 500 !important;
}

::v-deep .apexcharts-legend-series {
  margin-bottom: 2px !important;
}

/* =========================================================
   TABLE SECTION
========================================================= */

.table-section {
  padding: 18px 18px 18px 20px;
}

/* =========================================================
   CUSTOM TABLE
========================================================= */

.revenue-table {
  overflow: hidden;

  border: 1px solid #e5e7eb;
  border-radius: 10px;

  background: #ffffff;
}

/* =========================================================
   TABLE HEADER
========================================================= */

.table-head {
  display: grid;

  grid-template-columns:
    minmax(130px, 1.1fr)
    minmax(145px, 1fr)
    minmax(145px, 1fr)
    minmax(130px, 0.9fr);

  align-items: center;

  min-height: 42px;

  padding: 0 14px;

  background: #f8fafc;

  border-bottom: 1px solid #e5e7eb;

  color: #475569;

  font-size: 10px;
  font-weight: 700;

  white-space: nowrap;
}

/* =========================================================
   TABLE BODY
========================================================= */

.table-body {
  height: 335px;

  overflow-y: auto;
  overflow-x: hidden;
}

/* scrollbar */

.table-body::-webkit-scrollbar {
  width: 5px;
}

.table-body::-webkit-scrollbar-track {
  background: #f8fafc;
}

.table-body::-webkit-scrollbar-thumb {
  border-radius: 10px;

  background: #cbd5e1;
}

.table-body::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* =========================================================
   ROW
========================================================= */

.table-row {
  display: grid;

  grid-template-columns:
    minmax(130px, 1.1fr)
    minmax(145px, 1fr)
    minmax(145px, 1fr)
    minmax(130px, 0.9fr);

  align-items: center;

  min-height: 38px;

  padding: 0 14px;

  border-bottom: 1px solid #f1f5f9;

  color: #64748b;

  font-size: 10px;

  transition: background 0.15s ease, color 0.15s ease;
}

.table-row:hover {
  background: #f8fafc;

  color: #334155;
}

.table-row:last-child {
  border-bottom: 0;
}

/* Witel */

.col-witel {
  color: #334155;

  font-weight: 600;
}

/* Fee */

.fee-value {
  color: #b91c1c;
}

/* =========================================================
   TOTAL
========================================================= */

.table-total {
  display: grid;

  grid-template-columns:
    minmax(130px, 1.1fr)
    minmax(145px, 1fr)
    minmax(145px, 1fr)
    minmax(130px, 0.9fr);

  align-items: center;

  min-height: 46px;

  padding: 0 14px;

  border-top: 1px solid #dbe2ea;

  background: #f8fafc;

  color: #1e293b;

  font-size: 10px;
  font-weight: 700;

  white-space: nowrap;
}

/* =========================================================
   RESPONSIVE
========================================================= */

@media (max-width: 1199.98px) {
  .dashboard-content {
    grid-template-columns: 1fr;
  }

  .chart-section {
    border-right: 0;

    border-bottom: 1px solid #eef2f7;
  }

  .table-section {
    padding-top: 18px;
  }
}

@media (max-width: 575.98px) {
  .dashboard-card-header {
    align-items: flex-start;

    flex-direction: column;

    gap: 12px;
  }

  .year-filter {
    width: 100%;

    justify-content: space-between;
  }

  .dashboard-content {
    display: block;
  }

  .chart-section {
    padding: 10px;
  }

  .table-section {
    padding: 10px;
  }
}
</style>
